// Pure helpers for the report-share flow (PROP-14).
//
// Extracted from SendReportModal so the URL construction — the part
// with real correctness + injection-safety stakes — is unit-testable
// without mounting the modal. The side-effecting "open the client"
// wrapper stays in the component; everything here is pure.

import type { ReportShareChannel } from "./proagentApi";

// channelToWire normalises a UI channel label (as rendered in the
// modal's chip row) to the wire-shape token the backend records.
// Anything outside the documented set falls back to "link" so we
// never drop a row over an unexpected label.
export function channelToWire(label: string): ReportShareChannel {
  switch (label) {
    case "Email":
      return "email";
    case "WhatsApp":
      return "whatsapp";
    case "PDF attachment":
      return "pdf";
    default:
      return "link";
  }
}

// buildMailtoURL constructs a mailto: deep-link. Every interpolated
// value is percent-encoded, which is what prevents header injection:
// a newline in `subject` becomes %0A inside the encoded field rather
// than a raw CR/LF that a mail client could read as a CC:/BCC: header
// separator (RFC 6068 — clients do not un-encode and re-split).
export function buildMailtoURL(recipients: string, subject: string, body: string): string {
  const to = splitRecipients(recipients).join(",");
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// buildWhatsAppURL constructs a wa.me deep-link. The phone target is
// reduced to digits only (wa.me's required format), which also strips
// any injection payload; the body is percent-encoded. An empty/garbage
// recipient yields the bare wa.me/?text=... chooser form.
export function buildWhatsAppURL(recipients: string, body: string): string {
  const first = splitRecipients(recipients)[0] ?? "";
  const digits = first.replace(/[^\d]/g, "");
  const base = digits ? `https://wa.me/${digits}` : `https://wa.me/`;
  return `${base}?text=${encodeURIComponent(body)}`;
}

// splitRecipients turns the free-form comma list the agent typed into
// trimmed, non-empty entries. Shared by both builders so the "first
// recipient" semantics stay identical.
export function splitRecipients(recipients: string): string[] {
  return recipients
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
